import dayjs from 'dayjs'
import { match } from 'ts-pattern'
import { Divider, Text } from '@/components'
import { AlertMessage } from 'app-components'
import Battery from '@/app/features/electric-vehicles/charging-history/Battery'
import HourlyRateChart from '@/app/components/HourlyRateChart'
import {
  EnergyBreakdownLine,
  PlugSession,
  RatePeriod,
} from '@/types/plugSessions'

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
    amount,
  )
}

// Rates carry a third decimal when the BE needs it ($0.102/kWh): truncating
// to cents makes "9.0 kWh @ $0.10" sit beside a $0.92 line total
function formatRate(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  }).format(amount)
}

function breakdownTextClass(period: RatePeriod) {
  return (
    match(period)
      .with('off_peak', () => 'text-themed-green-700')
      .with('partial_peak', () => 'text-themed-orange-500')
      .with('peak', () => 'text-themed-red-600')
      .with('flat', () => 'text-themed-grey-500')
      // Real-time cost categories mirror the TOU palette
      .with('negative', 'low', () => 'text-themed-green-700')
      .with('medium', () => 'text-themed-orange-500')
      .with('high', () => 'text-themed-red-600')
      .exhaustive()
  )
}

type BatterySummaryProps = {
  session: PlugSession
}

// The shared charging-history Battery: proper outline, terminal nub, and the
// existing-charge vs added-charge fill segments
function BatterySummary({ session }: BatterySummaryProps) {
  return (
    <div className="py-2">
      <Battery charge={session} bordered />
    </div>
  )
}

type EnergyBreakdownProps = {
  lines: EnergyBreakdownLine[]
  currency: string
}

function EnergyBreakdown({ lines, currency }: EnergyBreakdownProps) {
  const isFlat = lines.length === 1 && lines[0].period === 'flat'
  // Total the lines themselves so the table is always internally consistent —
  // the session's stored total can come from a different pricing pipeline
  // than the per-hour telemetry costs
  const totalCost = lines.reduce((sum, line) => sum + line.cost, 0)
  const totalKwh =
    Math.round(lines.reduce((sum, line) => sum + line.kwh, 0) * 10) / 10

  return (
    <div className="flex flex-col gap-2 w-full">
      <Text variant="body1">Total Energy Costs</Text>
      {lines.map((line) => (
        <div
          key={`${line.period}-${line.label}`}
          className={`flex gap-4 items-start ${breakdownTextClass(
            line.period,
          )}`}
        >
          <Text variant="body4" className="whitespace-nowrap !text-inherit">
            {`${line.kwh} kWh${
              line.label ? ` ${line.label}` : ''
            } @${formatRate(line.rate_per_kwh, currency)} / kWh`}
          </Text>
          {/* Flat rate has no total row below, so its single line's cost IS
              the total — bold green like the chart bars */}
          <Text
            variant={isFlat ? 'body1' : 'body3'}
            className={`flex-1 text-right tabular-nums ${
              isFlat ? '!text-themed-green-700' : '!text-inherit'
            }`}
          >
            {formatCurrency(line.cost, currency)}
          </Text>
        </div>
      ))}
      {!isFlat && (
        <>
          <Divider />
          {/* body1: the totals read bolder and bigger than the tier lines */}
          <div className="flex gap-4 items-start">
            <Text variant="body1" className="flex-1">
              {`${totalKwh} kWh total`}
            </Text>
            <Text variant="body1" className="flex-1 text-right tabular-nums">
              {formatCurrency(totalCost, currency)}
            </Text>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Below ~2 kW a session is Level 1 (120 V) charging. L1 often cannot finish
 * overnight, so "plug in earlier / more frequently" coaching doesn't apply —
 * some peak-hour charging may be unavoidable and the banner is suppressed.
 */
const LEVEL_1_MAX_KW = 2

export function isLevel1Session(session: PlugSession) {
  // Use the peak charging power: the session-average charge_rate_kw is
  // idle-diluted below the L1 threshold even for L2 hardware
  const rate = session.max_charge_rate_kw ?? session.charge_rate_kw
  return rate > 0 && rate < LEVEL_1_MAX_KW
}

/**
 * Session dates: "November 10th" for a same-day session, a span like
 * "November 10–12" (or "November 30 – December 2") when it crosses midnight.
 * Sessions that didn't start in the current year (local time) append the
 * year ("November 10th, 2025"); a span across New Year's shows both years.
 * Shared by the session cards and the detail subtitle so a multi-day session
 * is identifiable everywhere while times stay clean.
 */
// "1st" / "2nd" / "3rd" / "4th" … "31st" — dayjs 'Do' needs the
// advancedFormat plugin; a local helper avoids a global extend
function ordinal(day: number) {
  if (day % 100 >= 11 && day % 100 <= 13) {
    return `${day}th`
  }
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] ?? 'th'
  return `${day}${suffix}`
}

export function formatSessionDates(session: PlugSession) {
  const start = dayjs(session.start_time)
  const end = dayjs(session.end_time)
  if (!start.isSame(end, 'year')) {
    return `${start.format('MMMM D, YYYY')} – ${end.format('MMMM D, YYYY')}`
  }
  const dates = start.isSame(end, 'day')
    ? `${start.format('MMMM')} ${ordinal(start.date())}`
    : start.isSame(end, 'month')
    ? `${start.format('MMMM D')}–${end.format('D')}`
    : `${start.format('MMMM D')} – ${end.format('MMMM D')}`
  return start.isSame(dayjs(), 'year')
    ? dates
    : `${dates}, ${start.format('YYYY')}`
}

/** Fractional hours -> "5.25 hrs" / "12 hrs" / "0.5 hrs" — quarter-hour
 *  card format; JS drops trailing zeros so 3.00 renders "3" */
export function formatQuarterHours(hours: number) {
  const quarters = Math.round(hours * 4) / 4
  return `${quarters} hr${quarters === 1 ? '' : 's'}`
}

/** Minute-truncated plug duration in fractional hours, sharing
 *  formatDuration's customer-visible-clock basis */
export function plugDurationHours(start: string, end: string) {
  return (
    dayjs(end)
      .startOf('minute')
      .diff(dayjs(start).startOf('minute'), 'minute') / 60
  )
}

/** Fractional hours -> "5 hrs, 21 min" — for computed durations like
 *  Charged For (Energy Used / Charge Rate) */
export function formatHoursDuration(hours: number) {
  const totalMinutes = Math.round(hours * 60)
  const wholeHours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const hourPart = `${wholeHours} hr${wholeHours === 1 ? '' : 's'}`
  if (wholeHours === 0) {
    return `${minutes} min`
  }
  return minutes === 0 ? hourPart : `${hourPart}, ${minutes} min`
}

/** "7 hrs, 15 min" / "3 hrs" / "45 min" — exact, never rounded to hours.
 *  Computed on minute-truncated times so it always agrees with the displayed
 *  clock times: 12:46 AM – 9:45 AM must read 8 hrs, 59 min even when the
 *  raw timestamps' seconds would shave it to 8 hrs, 58 min. */
export function formatDuration(start: string, end: string) {
  const totalMinutes = dayjs(end)
    .startOf('minute')
    .diff(dayjs(start).startOf('minute'), 'minute')
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const hourPart = `${hours} hr${hours === 1 ? '' : 's'}`
  if (hours === 0) {
    return `${minutes} min`
  }
  return minutes === 0 ? hourPart : `${hourPart}, ${minutes} min`
}

/** "Mon, Nov 10, 2:00 PM" — one endpoint of the session; the year appears
 *  only when it isn't the current one ("Mon, Nov 10, 2025, 2:00 PM") */
function formatDateTime(timestamp: string) {
  const d = dayjs(timestamp)
  const year = d.isSame(dayjs(), 'year') ? '' : `, ${d.format('YYYY')}`
  return `${d.format('ddd, MMM D')}${year}, ${d.format('h:mm A')}`
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-4 items-start">
      <Text variant="body4" className="whitespace-nowrap">
        {label}
      </Text>
      <Text variant="body1" className="flex-1 text-right tabular-nums">
        {value}
      </Text>
    </div>
  )
}

type Props = {
  session: PlugSession
  onUpdateSettings?: () => void
}

/**
 * Content of the plug session detail page. Header/back navigation is owned by
 * the routed page (PlugSessionDetailPage) — this renders only the body.
 */
export default function PlugSessionDetail({
  session,
  onUpdateSettings,
}: Props) {
  const currency = session.currency || 'USD'
  const isTou = session.rate_plan_type === 'tou'
  // The Ready-By coaching banner only earns its place when the session
  // charged outside the cheapest tier (partial peak or peak)
  const hasExpensiveCharging =
    session.hourly_costs?.some(
      (hour) =>
        (hour.period === 'peak' || hour.period === 'partial_peak') &&
        hour.kwh > 0,
    ) ?? false

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* The session's detail rows — the cards stay dense-free (quarter-hour
          stats), the exact figures live here */}
      <div className="flex flex-col gap-2 -mt-2">
        {/* Start = when the car was PLUGGED IN (not when charging began), so
            pauses — DR events, scheduling — read as gaps inside the session */}
        <DetailRow
          label="Start Date"
          value={formatDateTime(session.start_time)}
        />
        <DetailRow label="End Date" value={formatDateTime(session.end_time)} />
        <DetailRow
          label="Plugged In"
          value={formatDuration(session.start_time, session.end_time)}
        />
        {session.max_charge_rate_kw !== null &&
          session.max_charge_rate_kw > 0 && (
            <DetailRow
              label="Active Charge Time"
              value={formatHoursDuration(
                session.energy_kwh / session.max_charge_rate_kw,
              )}
            />
          )}
        {session.max_charge_rate_kw !== null && (
          <DetailRow
            label="Max Charge Rate"
            value={`${session.max_charge_rate_kw} kW`}
          />
        )}
      </div>

      {session.hourly_costs?.length ? (
        // Flat sessions classify every hour off_peak, so their bars render
        // in the same green as the flat rate's cost accents
        <HourlyRateChart
          hourlyCosts={session.hourly_costs}
          currency={currency}
        />
      ) : session.start_battery_percent !== null &&
        session.end_battery_percent !== null ? (
        <BatterySummary session={session} />
      ) : null}

      <Divider />

      {session.energy_breakdown && (
        <EnergyBreakdown lines={session.energy_breakdown} currency={currency} />
      )}

      {isTou &&
        session.ready_by_time &&
        hasExpensiveCharging &&
        !isLevel1Session(session) && (
          <AlertMessage variant="info">
            {`If you see any charging during expensive times, it was likely due to your Ready By time of ${session.ready_by_time}. `}
            <span
              className="underline underline-offset-2 cursor-pointer"
              onClick={onUpdateSettings}
            >
              Update your settings
            </span>
            {
              ' or plug in earlier and/or more frequently to avoid high-cost hours.'
            }
          </AlertMessage>
        )}
    </div>
  )
}
